import React, { useState, useEffect } from "react";
import { useLang } from "../i18n/LanguageContext";
import { Menu, X, Globe } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e6799e48-be90-493c-8434-b6147d09c3b6/artifacts/9k1prijt_image.png";
const PROFILE_LINK = "https://trackmypackage.app/join/shipcostarica";

const Header = () => {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#how", label: t.nav.how, id: "nav-link-how" },
    { href: "#pricing", label: t.nav.pricing, id: "nav-link-pricing" },
    { href: "#why", label: t.nav.why, id: "nav-link-why" },
    { href: "#contact", label: t.nav.contact, id: "nav-link-contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-brand-cream/85 border-b border-brand-navy/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
        <a href="#top" data-testid="header-logo-link" className="flex items-center gap-3">
          <img
            src={LOGO_URL}
            alt="Ship Costa Rica logo"
            className="h-12 w-12 rounded-xl object-cover ring-1 ring-brand-navy/10 shadow-sm"
          />
          <div className="leading-tight hidden sm:block">
            <div className="font-heading font-black text-brand-navy text-lg tracking-tight">
              SHIP COSTA RICA
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-brand-blue font-semibold">
              Air Express · Miami → CR
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={l.id}
              className="text-sm font-semibold text-brand-navy/80 hover:text-brand-red transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle-btn"
            onClick={toggle}
            className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-brand-navy hover:text-brand-red transition-colors px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-full border border-brand-navy/15 hover:border-brand-red/40"
            aria-label="Toggle language"
          >
            <Globe size={14} className="sm:hidden" />
            <Globe size={15} className="hidden sm:block" />
            <span className="sm:hidden">{lang === "en" ? "ES" : "EN"}</span>
            <span className="hidden sm:inline">{lang.toUpperCase()} / {lang === "en" ? "ES" : "EN"}</span>
          </button>
          <a
            href={PROFILE_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-cta-create-profile"
            className="hidden sm:inline-flex items-center justify-center rounded-full bg-brand-red text-white font-heading font-bold text-sm tracking-tight px-5 py-2.5 shadow-[0_6px_20px_-6px_rgba(216,35,42,0.6)] hover:bg-brand-navy hover:shadow-[0_6px_20px_-6px_rgba(14,42,86,0.7)] transition-all"
          >
            {t.nav.cta}
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((s) => !s)}
            className="lg:hidden p-2 rounded-lg text-brand-navy"
            aria-label="Open menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-brand-cream/95 backdrop-blur-xl border-b border-brand-navy/10">
          <div className="px-5 py-5 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.id}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-base font-semibold text-brand-navy"
              >
                {l.label}
              </a>
            ))}
            <button
              data-testid="lang-toggle-btn-mobile"
              onClick={() => {
                toggle();
                setOpen(false);
              }}
              className="self-start inline-flex items-center gap-2 text-sm font-bold text-brand-navy px-4 py-2 rounded-full border border-brand-navy/20"
            >
              <Globe size={15} />
              {lang === "en" ? "Español" : "English"}
            </button>
            <a
              href={PROFILE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-cta-create-profile"
              className="inline-flex items-center justify-center rounded-full bg-brand-red text-white font-heading font-bold text-sm px-5 py-3"
              onClick={() => setOpen(false)}
            >
              {t.nav.cta}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
